/**
 * useBadgePosition - Hook for centering badges on connection lines.
 *
 * Consolidates the badge size measurement and centering logic that was
 * previously duplicated in:
 * - unified-view/ApprovedLinkBadge.tsx
 * - unified-view/SuggestedLinkBadge.tsx
 */

import { useLayoutEffect, useRef, useState } from 'react';

import type { Point } from '../types/linking-view.types';

interface Size {
  width: number;
  height: number;
}

interface UseBadgePositionResult {
  /** Ref to attach to the badge element for size measurement */
  ref: React.RefObject<HTMLDivElement>;
  /** Computed style for centering the badge at the given position */
  style: {
    left: number;
    top: number;
  };
}

/**
 * Hook for positioning badges centered on a point.
 *
 * Measures the badge size after render and computes the position
 * to center the badge at the given coordinates.
 *
 * @param position - The center point where the badge should be positioned
 * @returns Object with ref to attach and computed style
 *
 * @example
 * const { ref, style } = useBadgePosition({ x: 100, y: 200 });
 * return <div ref={ref} style={style}>Badge</div>;
 */
export function useBadgePosition(position: Point): UseBadgePositionResult {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  // Measure the badge size after render
  useLayoutEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    }
  }, []);

  const style = {
    left: position.x - size.width / 2,
    top: position.y - size.height / 2,
  };

  return { ref, style };
}

export default useBadgePosition;
